import * as React from "react";

import { Button, Callout, H1, Intent } from "@blueprintjs/core";
import { css } from "glamor";
import produce from "immer";

import FightContainer from "./FightContainer";
import JurorArranger from "./JurorArranger";

import {
	getFightOverview,
	sendGrades,
	setCurrentPart,
	setCurrentPhase,
	setStageFinished,
} from "../api";
import { partsFight3Stages, Phase, phasesForTimer } from "../constants";
import { Grade, Participant, Stage } from "../Interfaces";
import Loading from "../Loading";
import FightOverview from "./FightOverview";
import GradeTable from "./GradeTable";
import OverviewContainer from "./OverviewContainer";
import TimerContainer from "./TimerContainer";
import TimerPhase from "./TimerPhase";

interface State {
	currentPhase: Phase;
	currentPart: number;
	endTimes: number[];
}

interface Props {
	fightContainer: FightContainer;
	fightId: number;
	dark: boolean;
}

const styles = {
	startFightButton: css({
		margin: "30px",
	}),
	fightOverCallout: css({
		textAlign: "center",
		width: "300px",
		display: "inline-block",
		margin: "20px auto",
	}),
	fightOverCalloutWrapper: css({ textAlign: "center", width: "100%" }),
};

export default class FightPage extends React.Component<Props, State> {
	public state = {
		currentPhase: 0,
		currentPart: 0,
		endTimes: [0],
	};

	public async componentWillMount() {
		this.props.fightContainer.init(this.props.fightId);
		const initialData = await getFightOverview(this.props.fightId);
		this.setState({
			currentPhase: initialData.currentphase,
			currentPart: initialData.currentpart,
		});
	}

	private createPhaseView() {
		switch (this.state.currentPhase) {
			case Phase.fightToStart: {
				return (
					<OverviewContainer
						steps={partsFight3Stages.map((part) => part.name)}
						currentIndex={this.state.currentPart}
						dark={this.props.dark}
					>
						<FightOverview
							teams={this.props.fightContainer.state.fight.teams}
							participants={this.props.fightContainer.state.fight.participants}
							stages={this.props.fightContainer.state.fight.stages}
							currentstage={partsFight3Stages[this.state.currentPart].stagenumber!}
						/>
						<Button {...styles.startFightButton} onClick={() => this.incrementCurrentPhase(0)}>
							Start Fight
						</Button>
					</OverviewContainer>
				);
				break;
			}
			case Phase.presentationReporter:
			case Phase.questionsOfOpponent:
			case Phase.prepForOpponent:
			case Phase.statementOfOpponent:
			case Phase.discussion:
			case Phase.summaryOfDiscussion:
			case Phase.questionsOfReviewer:
			case Phase.prepForReviewer:
			case Phase.review:
			case Phase.concludingRemarksReporter:
			case Phase.questionsOfJury: {
				const stage = this.props.fightContainer.state.fight.stages[
					partsFight3Stages[this.state.currentPart].stagenumber! - 1
				]! as Stage;
				const participants = this.props.fightContainer.state.fight.participants;
				const teams = this.props.fightContainer.state.fight.teams;
				const allParticipants: Participant[] = [];
				Object.keys(participants)
					.map((key) => participants[key])
					.forEach((participant) => {
						allParticipants.push(participant);
					});
				const reporter = allParticipants.find(
					(participant) => participant.id === stage.reporter_id
				) as Participant;
				const opponent = allParticipants.find(
					(participant) => participant.id === stage.opponent_id
				) as Participant;
				const reviewer = allParticipants.find(
					(participant) => participant.id === stage.reviewer_id
				) as Participant;
				return stage ? (
					<TimerContainer
						phases={phasesForTimer.map((phase) => phase.name).slice(1)}
						currentIndex={this.state.currentPhase - 1}
						stagenumber={partsFight3Stages[this.state.currentPart].stagenumber!}
						reporter={reporter.firstname + " " + reporter.lastname}
						opponent={opponent.firstname + " " + opponent.lastname}
						reviewer={reviewer.firstname + " " + reviewer.lastname}
						reporterTeamname={teams[reporter.team_id].name}
						opponentTeamname={teams[opponent.team_id].name}
						reviewerTeamname={teams[reviewer.team_id].name}
						dark={this.props.dark}
					>
						<TimerPhase
							name={phasesForTimer[this.state.currentPhase].name}
							duration={phasesForTimer[this.state.currentPhase].duration}
							nextPhase={this.incrementCurrentPhase}
							previousPhase={this.decrementCurrentPhase}
							key={this.state.currentPart * 20 + this.state.currentPhase}
							startTime={this.state.endTimes[this.state.currentPhase]}
							canGoBack={true}
							dark={this.props.dark}
						/>
					</TimerContainer>
				) : (
					<Loading />
				);
				break;
			}
			case Phase.grading: {
				return (
					<div>
						<GradeTable
							jurors={this.props.fightContainer.state.fight.jurors}
							participants={this.props.fightContainer.state.fight.participants}
							stage={
								this.props.fightContainer.state.fight.stages[
									partsFight3Stages[this.state.currentPart].stagenumber! - 1
								]
							}
							onGradeSubmission={this.onGradeSubmission}
						/>
					</div>
				);
				break;
			}
			default:
				return <div>oops</div>;
		}
	}
	private createPartView() {
		switch (partsFight3Stages[this.state.currentPart].type) {
			case "setup": {
				return (
					<div>
						{
							<OverviewContainer
								steps={partsFight3Stages.map((part) => part.name)}
								currentIndex={this.state.currentPart}
								dark={this.props.dark}
							>
								<div>
									<H1 style={{ padding: "0 25px", textAlign: "left" }}>Arrange Jurors:</H1>
									<JurorArranger
										onRearrange={this.props.fightContainer.setJurorOrder}
										jurors={this.props.fightContainer.state.fight.jurors}
										disableArranging={false}
									/>
									<Button onClick={this.incrementCurrentPart}>Start Fight</Button>
								</div>
							</OverviewContainer>
						}
					</div>
				);
			}
			case "stage": {
				return <div>{this.createPhaseView()}</div>;
			}
			case "break": {
				return (
					<OverviewContainer
						steps={partsFight3Stages.map((part) => part.name)}
						currentIndex={this.state.currentPart}
						dark={this.props.dark}
					>
						<TimerPhase
							name={"Break"}
							duration={10 * 60}
							nextPhase={this.incrementCurrentPart}
							previousPhase={() => {
								return;
							}}
							startTime={0}
							canGoBack={false}
							dark={this.props.dark}
						/>
					</OverviewContainer>
				);
			}
			case "finished": {
				return (
					<OverviewContainer
						steps={partsFight3Stages.map((part) => part.name)}
						currentIndex={this.state.currentPart}
						dark={this.props.dark}
					>
						<div {...styles.fightOverCalloutWrapper}>
							<div {...styles.fightOverCallout}>
								<Callout intent={Intent.SUCCESS} title={"The fight is over!"} />
							</div>
						</div>
					</OverviewContainer>
				);
			}
			default: {
				return <div>oops</div>;
			}
		}
	}

	private onGradeSubmission = (stageId: number, grades: Grade[]) => {
		const password = localStorage.getItem("sypt_fight_password");
		const fight_id = this.props.fightId;
		if (!password) {
			return;
		}
		sendGrades(fight_id, password, stageId, grades);
		setStageFinished(
			fight_id,
			password,
			(this.props.fightContainer.state.fight.stages.sort(
				(a: Stage, b: Stage) => a.stagenumber - b.stagenumber
			)[partsFight3Stages[this.state.currentPart].stagenumber! - 1] as Stage).id
		);
		this.incrementCurrentPart();
	};

	private incrementCurrentPhase = async (timeLeft: number) => {
		const password = localStorage.getItem("sypt_fight_password");
		const fight_id = this.props.fightId;
		if (!password) {
			return;
		}
		if (this.state.currentPhase) {
			this.setState(
				produce((state) => {
					state.endTimes[this.state.currentPhase] =
						phasesForTimer[this.state.currentPhase].duration - timeLeft;
				})
			);
		}
		const newCurrentPhase = this.state.currentPhase + 1;
		const response = await setCurrentPhase(fight_id, password, newCurrentPhase);
		const success = response.length;

		if (success) {
			this.setState(
				produce((state) => {
					state.currentPhase = newCurrentPhase;
				})
			);
		}
	};
	private decrementCurrentPhase = async () => {
		const password = localStorage.getItem("sypt_fight_password");
		const fight_id = this.props.fightId;
		if (!password) {
			return;
		}
		this.setState(
			produce((state) => {
				state.endTimes[this.state.currentPhase] = 0;
			})
		);
		const newCurrentPhase = this.state.currentPhase - 1;
		const response = await setCurrentPhase(fight_id, password, newCurrentPhase);
		const success = response.length;

		if (success) {
			this.setState(
				produce((state) => {
					state.currentPhase = newCurrentPhase;
				})
			);
		}
	};
	private incrementCurrentPart = async () => {
		const password = localStorage.getItem("sypt_fight_password");
		const fight_id = this.props.fightId;
		if (!password) {
			return;
		}
		const newCurrentPart = this.state.currentPart + 1;
		const responsePhase = await setCurrentPhase(fight_id, password, 0);
		const responsePart = await setCurrentPart(fight_id, password, newCurrentPart);

		const successPart = responsePart.length;
		const successPhase = responsePhase.length;
		if (successPhase && successPart) {
			this.setState(
				produce((state) => {
					state.currentPart = newCurrentPart;
					state.currentPhase = 0;
					state.endTimes = [0];
				})
			);
		}
	};
	public render() {
		return (
			<div>
				{this.props.fightContainer.state.fight.stages.length >= 1 ? (
					this.createPartView()
				) : (
					<Loading />
				)}
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/FightRoom/FightPage.tsx